import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const GalleryContainer = styled.div`
  padding: 100px 50px 50px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 20px 30px;
  }
`;

const Title = styled.h1`
  text-align: center;
  color: #1d3557;
  margin-bottom: 40px;
  font-size: 3rem;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  animation: ${fadeIn} 1s ease-out;
  animation-delay: 0.2s;
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 25px;
  background-color: ${props => props.$active ? '#1d3557' : '#fff'};
  color: ${props => props.$active ? '#fff' : '#1d3557'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
`;

const ImageCard = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  height: 300px;
  animation: ${fadeIn} 1s ease-out;
  animation-delay: ${props => props.$delay}s;
  
  &:hover img {
    transform: scale(1.1);
  }

  &:hover .overlay {
    opacity: 1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(29, 53, 87, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  padding: 20px;
  color: white;
  text-align: center;
`;

const BackButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  background-color: #e63946;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  }
`;

const ReturnButton = styled.button`
  position: fixed;
  top: 100px;
  left: 30px;
  background-color: #1d3557;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 25px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  font-weight: bold;
  z-index: 100;

  &:hover {
    transform: translateX(-3px);
    background-color: #e63946;
    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  }

  @media (max-width: 768px) {
    top: 90px;
    left: 20px;
    padding: 10px 20px;
  }
`;

const GalleryPage = () => {
  const [filter, setFilter] = useState('all');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const navigate = useNavigate();

  const galleryItems = [
    {
      id: 1,
      image: 'https://images.unsplash.com/photo-1515378960530-7c0da6231fb1',
      title: 'Tech Expo 2024',
      description: 'Présentation de nos derniers produits',
      category: 'evenements'
    },
    {
      id: 2,
      image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902',
      title: 'Nouveau Magasin',
      description: 'Inauguration',
      category: 'magasins'
    }
  ];

  const filteredItems = filter === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === filter);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReturn = () => {
    navigate('/');
  };

  return (
    <GalleryContainer>
      <ReturnButton onClick={handleReturn}>
        ← Retour à l'accueil
      </ReturnButton>
      
      <Title>Galerie</Title>
      
      <FilterContainer>
        <FilterButton 
          $active={filter === 'all'} 
          onClick={() => setFilter('all')}
        >
          Tout
        </FilterButton>
        <FilterButton 
          $active={filter === 'evenements'} 
          onClick={() => setFilter('evenements')}
        >
          Événements
        </FilterButton>
        <FilterButton 
          $active={filter === 'magasins'} 
          onClick={() => setFilter('magasins')}
        >
          Magasins
        </FilterButton>
      </FilterContainer>

      <GalleryGrid>
        {filteredItems.map((item, index) => (
          <ImageCard 
            key={item.id}
            $delay={index * 0.2}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <Image src={item.image} alt={item.title} />
            <Overlay className="overlay">
              <h3 style={{ marginBottom: '10px', fontSize: '1.5rem' }}>{item.title}</h3>
              <p>{item.description}</p>
            </Overlay>
          </ImageCard>
        ))}
      </GalleryGrid>

      <BackButton onClick={handleBackToTop}>↑</BackButton>
    </GalleryContainer>
  );
};

export default GalleryPage;